const {
  applyPushResult,
  applySnapshot,
  replaceWithSnapshot,
  copyDeletedTodos,
} = require("../../shared/syncState.js");

const DOC_STORES = {
  todo_list: "todo_lists",
  repeating_event: "repeating_events",
  repeating_event_by_date: "repeating_events_by_date",
};

const LOCAL_KEYS = {
  config: "config",
  custom_list_ids: "customTodoListIds",
};

const IDB_STORES = ["todo_lists", "repeating_events", "repeating_events_by_date"];

function shouldReplaceCache(snapshotResponse) {
  return snapshotResponse.ok === true;
}

async function api(path, options = {}) {
  let response;
  try {
    response = await fetch(path, {
      ...options,
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...(options.headers || {}),
      },
    });
  } catch {
    throw new Error("无法连接服务器");
  }
  return response;
}

async function readBody(response) {
  try {
    return await response.json();
  } catch {
    return {};
  }
}

async function fetchSession() {
  const response = await api("/api/me");
  if (response.status === 401) return null;
  const json = await readBody(response);
  if (!response.ok) throw new Error(json.error || "请求失败");
  return json;
}

async function fetchAdminUsers({ page = 1, pageSize = 50 } = {}) {
  const params = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
  });
  const response = await api(`/api/admin/users?${params}`);
  const json = await readBody(response);
  if (!response.ok) throw new Error(json.error || "请求失败");
  return json;
}

function throwApiError(response, json, fallbackMessage) {
  const err = new Error((json && json.error) || fallbackMessage);
  err.status = response.status;
  throw err;
}

async function fetchNotes({ limit = 20, cursor, tag, from, to, todoId, excludeTags } = {}) {
  const params = new URLSearchParams();
  params.set("limit", String(limit));
  if (cursor) params.set("cursor", cursor);
  if (tag) params.set("tag", tag);
  if (from) params.set("from", from);
  if (to) params.set("to", to);
  if (todoId) params.set("todoId", todoId);
  if (Array.isArray(excludeTags) && excludeTags.length) {
    params.set("excludeTags", excludeTags.join(","));
  }
  const response = await api(`/api/notes?${params}`);
  const json = await readBody(response);
  if (!response.ok) throwApiError(response, json, "加载笔记失败");
  return json;
}

async function fetchNoteTags() {
  const response = await api("/api/notes/tags");
  const json = await readBody(response);
  if (!response.ok) throwApiError(response, json, "加载标签失败");
  return json;
}

async function fetchNote(id) {
  const response = await api(`/api/notes/${encodeURIComponent(id)}`);
  const json = await readBody(response);
  if (!response.ok) throwApiError(response, json, "加载笔记失败");
  return json;
}

async function createNote({ body, todoId } = {}) {
  const payload = { body };
  if (todoId) payload.todoId = todoId;
  const response = await api("/api/notes", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  const json = await readBody(response);
  if (!response.ok) throwApiError(response, json, "创建笔记失败");
  return json;
}

async function updateNote(id, { body, baseRevision }) {
  const response = await api(`/api/notes/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify({ body, baseRevision }),
  });
  const json = await readBody(response);
  if (!response.ok) {
    const err = new Error((json && json.error) || "更新笔记失败");
    err.status = response.status;
    if (response.status === 409 && json && typeof json.id === "string") {
      err.note = json;
    }
    throw err;
  }
  return json;
}

async function deleteNote(id) {
  const response = await api(`/api/notes/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
  if (response.status === 204) return undefined;
  const json = await readBody(response);
  if (!response.ok) throwApiError(response, json, "删除笔记失败");
  return json;
}

async function deleteNotesByTodoId(todoId) {
  const params = new URLSearchParams({ todoId: String(todoId) });
  const response = await api(`/api/notes?${params}`, { method: "DELETE" });
  if (response.status === 204) return undefined;
  const json = await readBody(response);
  if (!response.ok) throwApiError(response, json, "清理任务笔记失败");
  return json;
}

async function submitAuth(path, email, password) {
  const response = await api(path, {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
  const json = await readBody(response);
  if (!response.ok) throw new Error(json.error || "请求失败");
  return json;
}

function login(email, password) {
  return submitAuth("/api/login", email, password);
}

function register(email, password) {
  return submitAuth("/api/register", email, password);
}

async function logout() {
  const response = await api("/api/logout", { method: "POST", body: "{}" });
  const json = await readBody(response);
  if (!response.ok) throw new Error(json.error || "请求失败");
  return json;
}

async function updateEmail(email) {
  const response = await api("/api/me/email", {
    method: "PATCH",
    body: JSON.stringify({ email }),
  });
  const json = await readBody(response);
  if (!response.ok) throw new Error(json.error || "请求失败");
  return json;
}

async function updatePassword(currentPassword, newPassword) {
  const response = await api("/api/me/password", {
    method: "POST",
    body: JSON.stringify({ currentPassword, newPassword }),
  });
  const json = await readBody(response);
  if (!response.ok) throw new Error(json.error || "请求失败");
  return json;
}

function openDatabase() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open("weekToDo", 4);
    request.onupgradeneeded = function (event) {
      const db = event.target.result;
      for (const name of IDB_STORES) {
        if (!db.objectStoreNames.contains(name)) {
          db.createObjectStore(name, { autoIncrement: false });
        }
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function withStores(db, fn) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORES, "readwrite");
    fn(tx);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error || new Error("IndexedDB 事务中止"));
  });
}

async function fetchTodoListsRange(from, to) {
  const response = await api(`/api/todo-lists?from=${from}&to=${to}`);
  const json = await readBody(response);
  if (!response.ok) throw new Error(json.error || "请求失败");
  return json;
}

async function fetchBootstrap() {
  const response = await api("/api/bootstrap");
  const json = await readBody(response);
  if (!response.ok) throw new Error(json.error || "请求失败");
  return json;
}

async function getDocument(docType, docId) {
  const response = await api(documentPath(docType, docId));
  const json = await readBody(response);
  if (response.status === 404) return null;
  if (!response.ok) throw new Error(json.error || "请求失败");
  return json;
}

async function putDocument(docType, docId, payload, baseRevision) {
  const response = await api(documentPath(docType, docId), {
    method: "PUT",
    body: JSON.stringify({ payload, baseRevision }),
  });
  const json = await readBody(response);
  if (response.status === 409) {
    return {
      conflict: true,
      payload: json.payload,
      revision: json.revision,
      updatedAt: json.updatedAt,
    };
  }
  if (!response.ok) throw new Error(json.error || "请求失败");
  return {
    conflict: false,
    payload: json.payload ?? payload,
    revision: json.revision,
    updatedAt: json.updatedAt,
  };
}

async function deleteDocument(docType, docId, baseRevision) {
  const rev = typeof baseRevision === "number" ? baseRevision : 0;
  const response = await api(
    `${documentPath(docType, docId)}?baseRevision=${encodeURIComponent(String(rev))}`,
    { method: "DELETE" }
  );
  if (response.status === 204) {
    return { conflict: false, deleted: true };
  }
  const json = await readBody(response);
  if (response.status === 409) {
    return {
      conflict: true,
      payload: json.payload,
      revision: json.revision,
      updatedAt: json.updatedAt,
    };
  }
  if (response.status === 404) {
    return { conflict: false, deleted: true };
  }
  if (!response.ok) throw new Error((json && json.error) || "删除失败");
  return { conflict: false, deleted: true };
}

async function pullAndReplaceCache() {
  const response = await api("/api/snapshot");
  const json = await readBody(response);
  const documents = Array.isArray(json.documents) ? json.documents : [];
  const snapshotResponse = { ok: response.ok, documents };
  if (!shouldReplaceCache(snapshotResponse)) {
    throw new Error(json.error || "拉取失败");
  }

  const db = await openDatabase();
  try {
    await withStores(db, (tx) => {
      for (const name of IDB_STORES) tx.objectStore(name).clear();
    });
    localStorage.removeItem("config");
    localStorage.removeItem("customTodoListIds");
    localStorage.removeItem("syncMeta");
    await withStores(db, (tx) => {
      for (const document of documents) {
        const table = DOC_STORES[document.docType];
        if (!table) continue;
        tx.objectStore(table).put(document.payload, document.docId);
      }
    });
  } finally {
    db.close();
  }

  for (const document of documents) {
    const key = LOCAL_KEYS[document.docType];
    if (!key) continue;
    localStorage.setItem(key, JSON.stringify(document.payload));
  }
  let previousMeta = { docs: {}, deletes: [], deletedTodos: {} };
  try {
    const raw = localStorage.getItem("syncMeta");
    if (raw) previousMeta = JSON.parse(raw);
  } catch (_) {
    /* keep empty previous */
  }
  localStorage.setItem("syncMeta", JSON.stringify(replaceWithSnapshot(documents, previousMeta)));
  return documents;
}

/**
 * Stable flush ordering for one tick.
 *
 * Push (PUT): repeating_event → repeating_event_by_date → todo_list → other
 *   so rules and generation markers land before day lists.
 * Delete: repeating_event_by_date → repeating_event → other → todo_list
 *   so related markers are removed before the day list when both are pending.
 *
 * @param {Array<{docType: string, docId: string, baseRevision?: number}>} docs
 * @param {'push'|'delete'} [mode='push']
 * @returns {typeof docs}
 */
function flushOrder(docs, mode = "push") {
  const list = Array.isArray(docs) ? docs : [];
  const pushRank = (docType) => {
    if (docType === "repeating_event") return 0;
    if (docType === "repeating_event_by_date") return 1;
    if (docType === "todo_list") return 2;
    return 3;
  };
  // Prefer deleting markers before todo_list when both are pending.
  const deleteRank = (docType) => {
    if (docType === "repeating_event_by_date") return 0;
    if (docType === "repeating_event") return 1;
    if (docType === "todo_list") return 3;
    return 2;
  };
  const rank = mode === "delete" ? deleteRank : pushRank;
  return list.slice().sort((a, b) => {
    const byType = rank(a.docType) - rank(b.docType);
    if (byType !== 0) return byType;
    return String(a.docId).localeCompare(String(b.docId));
  });
}

function nextFlushOrder(meta) {
  const pushes = [];
  const docs = meta && meta.docs ? meta.docs : {};
  for (const key of Object.keys(docs)) {
    const entry = docs[key];
    if (!entry || entry.dirty !== true) continue;
    const separator = key.indexOf(":");
    pushes.push({
      docType: key.slice(0, separator),
      docId: key.slice(separator + 1),
      baseRevision: entry.baseRevision,
    });
  }
  const deletes = meta && meta.deletes ? meta.deletes : [];
  return {
    pushes: flushOrder(pushes, "push"),
    deletes: flushOrder(deletes, "delete"),
  };
}

function cloneMeta(meta) {
  const docs = {};
  const source = meta && meta.docs ? meta.docs : {};
  for (const key of Object.keys(source)) {
    docs[key] = { baseRevision: source[key].baseRevision, dirty: source[key].dirty };
  }
  const deletes = (meta && meta.deletes ? meta.deletes : []).map((item) => ({
    docType: item.docType,
    docId: item.docId,
    baseRevision: item.baseRevision,
  }));
  // Must keep tombstones across flush — otherwise deleted todos reappear on pull/merge.
  return { docs, deletes, deletedTodos: copyDeletedTodos(meta) };
}

function hasPending(meta) {
  const docs = meta && meta.docs ? meta.docs : {};
  for (const key of Object.keys(docs)) {
    if (docs[key] && docs[key].dirty) return true;
  }
  return Boolean(meta && meta.deletes && meta.deletes.length);
}

function publishSyncStatus(meta) {
  if (typeof window === "undefined" || typeof window.dispatchEvent !== "function") return;
  window.dispatchEvent(new CustomEvent("sync-status", { detail: { pending: hasPending(meta) } }));
}

function documentPath(docType, docId) {
  return `/api/documents/${encodeURIComponent(docType)}/${encodeURIComponent(docId)}`;
}

function responseDoc(status, body) {
  if (status !== 200 && status !== 409) return undefined;
  if (!body || typeof body.revision !== "number") return undefined;
  return body;
}

function samePayload(left, right) {
  return JSON.stringify(left) === JSON.stringify(right);
}

function readLatestMeta(fallback) {
  if (typeof localStorage === "undefined") return fallback;
  const raw = localStorage.getItem("syncMeta");
  if (!raw) return fallback;
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function mergeLiveDirty(working, latest) {
  const latestDocs = latest && latest.docs ? latest.docs : {};
  if (!working.docs) working.docs = {};
  for (const key of Object.keys(latestDocs)) {
    const latestEntry = latestDocs[key];
    if (!latestEntry || latestEntry.dirty !== true) continue;
    const current = working.docs[key];
    working.docs[key] = {
      baseRevision: current ? current.baseRevision : latestEntry.baseRevision,
      dirty: true,
    };
  }
  return working;
}

function adoptNewDeletes(working, latest, capturedMeta) {
  if (!working.deletes) working.deletes = [];
  const capturedDeletes = capturedMeta && capturedMeta.deletes ? capturedMeta.deletes : [];
  for (const item of latest.deletes || []) {
    const same = (entry) => entry.docType === item.docType && entry.docId === item.docId;
    if (capturedDeletes.some(same)) continue;
    if (working.deletes.some(same)) continue;
    working.deletes.push({
      docType: item.docType,
      docId: item.docId,
      baseRevision: item.baseRevision,
    });
  }
}

function metaAfterPartialFlush(working, latest, capturedMeta) {
  const saved = cloneMeta(working);
  mergeLiveDirty(saved, latest);
  adoptNewDeletes(saved, latest, capturedMeta);
  return saved;
}

function adoptEditsSince(working, capturedMeta) {
  const saved = metaAfterPartialFlush(working, readLatestMeta(capturedMeta), capturedMeta);
  working.docs = saved.docs;
  working.deletes = saved.deletes;
}

function writeStoredMeta(meta) {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem("syncMeta", JSON.stringify(meta));
}

function storeResolvedDoc(docType, docId, baseRevision, dirty, fallback) {
  if (typeof localStorage === "undefined") return;
  const stored = cloneMeta(readLatestMeta(fallback));
  stored.docs[`${docType}:${docId}`] = { baseRevision, dirty: dirty === true };
  writeStoredMeta(stored);
}

function storeAcceptedDelete(docType, docId, fallback) {
  if (typeof localStorage === "undefined") return;
  const stored = cloneMeta(readLatestMeta(fallback));
  delete stored.docs[`${docType}:${docId}`];
  stored.deletes = stored.deletes.filter((item) => item.docType !== docType || item.docId !== docId);
  writeStoredMeta(stored);
}

function blockedLocally(meta, docType, docId) {
  const key = `${docType}:${docId}`;
  const local = meta.docs[key];
  if (local && local.dirty) return true;
  return meta.deletes.some((item) => item.docType === docType && item.docId === docId);
}

function readDoc(docType, docId, readLocal) {
  if (readLocal) return readLocal(docType, docId);
  return readStoredPayload(docType, docId);
}

async function readStoredPayload(docType, docId) {
  const localKey = LOCAL_KEYS[docType];
  if (localKey) {
    const raw = localStorage.getItem(localKey);
    if (raw == null) return null;
    return JSON.parse(raw);
  }
  const table = DOC_STORES[docType];
  if (!table) return null;
  const db = await openDatabase();
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(table, "readonly");
      const request = tx.objectStore(table).get(docId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  } finally {
    db.close();
  }
}

async function persistFlush(conflictWrites, documents, working, nextMeta) {
  const writes = conflictWrites.slice();
  for (const document of documents) {
    if (blockedLocally(working, document.docType, document.docId)) continue;
    writes.push({ docType: document.docType, docId: document.docId, payload: document.payload });
  }

  const removals = [];
  if (documents.length > 0) {
    const serverKeys = new Set(documents.map((document) => `${document.docType}:${document.docId}`));
    for (const key of Object.keys(working.docs)) {
      if (serverKeys.has(key)) continue;
      if (working.docs[key].dirty) continue;
      const separator = key.indexOf(":");
      const docType = key.slice(0, separator);
      const docId = key.slice(separator + 1);
      if (working.deletes.some((item) => item.docType === docType && item.docId === docId)) continue;
      removals.push({ docType, docId });
    }
  }

  const idbWrites = writes.filter((item) => DOC_STORES[item.docType]);
  const idbRemovals = removals.filter((item) => DOC_STORES[item.docType]);
  if (idbWrites.length || idbRemovals.length) {
    const db = await openDatabase();
    try {
      await withStores(db, (tx) => {
        for (const item of idbWrites) {
          tx.objectStore(DOC_STORES[item.docType]).put(item.payload, item.docId);
        }
        for (const item of idbRemovals) {
          tx.objectStore(DOC_STORES[item.docType]).delete(item.docId);
        }
      });
    } finally {
      db.close();
    }
  }

  for (const item of writes) {
    const localKey = LOCAL_KEYS[item.docType];
    if (!localKey) continue;
    localStorage.setItem(localKey, JSON.stringify(item.payload));
  }
  for (const item of removals) {
    const localKey = LOCAL_KEYS[item.docType];
    if (!localKey) continue;
    localStorage.removeItem(localKey);
  }
  localStorage.setItem("syncMeta", JSON.stringify(nextMeta));
}

async function markChangedPayloads(working, sentPayload, readLocal) {
  for (const [key, sent] of sentPayload) {
    if (!working.docs[key]) continue;
    const separator = key.indexOf(":");
    const current = await readDoc(key.slice(0, separator), key.slice(separator + 1), readLocal);
    if (!samePayload(current, sent)) {
      working.docs[key] = { baseRevision: working.docs[key].baseRevision, dirty: true };
    }
  }
}

async function flush(meta, readLocal, options = {}) {
  const working = cloneMeta(meta);
  const order = nextFlushOrder(meta);
  const hasWork = order.pushes.length > 0 || (order.deletes && order.deletes.length > 0);
  if (!hasWork && !options.pull) {
    publishSyncStatus(working);
    return { conflicts: [], meta: working };
  }

  const conflicts = [];
  const conflictWrites = [];
  const sentPayload = new Map();
  let acceptedWrite = false;

  try {
    for (const push of order.pushes) {
      const payload = await readDoc(push.docType, push.docId, readLocal);
      let response = await api(documentPath(push.docType, push.docId), {
        method: "PUT",
        body: JSON.stringify({ payload, baseRevision: push.baseRevision }),
      });
      let body = await readBody(response);
      let doc = responseDoc(response.status, body);
      const key = `${push.docType}:${push.docId}`;
      let result = applyPushResult(working, push.docType, push.docId, { status: response.status, doc });
      if (response.status === 200 || response.status === 409) sentPayload.set(key, payload);
      if (result && result.conflict && LOCAL_KEYS[push.docType] && doc && typeof doc.revision === "number") {
        response = await api(documentPath(push.docType, push.docId), {
          method: "PUT",
          body: JSON.stringify({ payload, baseRevision: doc.revision }),
        });
        body = await readBody(response);
        doc = responseDoc(response.status, body);
        result = applyPushResult(working, push.docType, push.docId, { status: response.status, doc });
        if (response.status === 200 || response.status === 409) sentPayload.set(key, payload);
      }
      if (result && result.conflict) {
        const current = working.docs[key] ? await readDoc(push.docType, push.docId, readLocal) : payload;
        const edited = working.docs[key] && !samePayload(current, payload);
        if (edited) {
          working.docs[key] = { baseRevision: working.docs[key].baseRevision, dirty: true };
          storeResolvedDoc(push.docType, push.docId, working.docs[key].baseRevision, true, meta);
        } else {
          conflicts.push({ docType: push.docType, docId: push.docId });
          if (doc && Object.prototype.hasOwnProperty.call(doc, "payload")) {
            conflictWrites.push({ docType: push.docType, docId: push.docId, payload: doc.payload });
          }
          if (doc && working.docs[key]) {
            storeResolvedDoc(push.docType, push.docId, working.docs[key].baseRevision, false, meta);
          }
        }
      } else if (response.status === 200 && doc && working.docs[key]) {
        acceptedWrite = true;
        const current = await readDoc(push.docType, push.docId, readLocal);
        if (!samePayload(current, payload)) {
          working.docs[key] = { baseRevision: working.docs[key].baseRevision, dirty: true };
        }
        // Publish the accepted revision now so a later local edit is visible as dirty.
        storeResolvedDoc(push.docType, push.docId, working.docs[key].baseRevision, working.docs[key].dirty === true, meta);
      }
    }

    for (const item of order.deletes || []) {
      const response = await api(
        `${documentPath(item.docType, item.docId)}?baseRevision=${encodeURIComponent(item.baseRevision)}`,
        { method: "DELETE" }
      );
      const body = response.status === 204 ? {} : await readBody(response);
      const doc = responseDoc(response.status, body);
      const result = applyPushResult(working, item.docType, item.docId, { status: response.status, doc });
      if (response.status === 204) {
        acceptedWrite = true;
        storeAcceptedDelete(item.docType, item.docId, meta);
      } else if (result && result.conflict) {
        conflicts.push({ docType: item.docType, docId: item.docId });
        if (doc && Object.prototype.hasOwnProperty.call(doc, "payload")) {
          conflictWrites.push({ docType: item.docType, docId: item.docId, payload: doc.payload });
        }
        const key = `${item.docType}:${item.docId}`;
        if (doc && working.docs[key]) {
          storeResolvedDoc(item.docType, item.docId, working.docs[key].baseRevision, false, meta);
        }
      }
    }

    await markChangedPayloads(working, sentPayload, readLocal);
    adoptEditsSince(working, meta);

    let nextMeta = working;
    if (options.pull) {
      const response = await api("/api/snapshot");
      const json = await readBody(response);
      if (!response.ok) throw new Error(json.error || "拉取失败");
      const documents = Array.isArray(json.documents) ? json.documents : [];
      nextMeta = applySnapshot(working, documents);
      await persistFlush(conflictWrites, documents, working, nextMeta);
    } else {
      await persistFlush(conflictWrites, [], working, working);
    }
    publishSyncStatus(nextMeta);
    return { conflicts, meta: nextMeta };
  } catch (error) {
    if (acceptedWrite) {
      const saved = metaAfterPartialFlush(working, readLatestMeta(meta), meta);
      writeStoredMeta(saved);
      publishSyncStatus(saved);
    } else {
      publishSyncStatus(meta);
    }
    throw error;
  }
}

module.exports = {
  shouldReplaceCache,
  fetchSession,
  fetchAdminUsers,
  fetchNotes,
  fetchNoteTags,
  fetchNote,
  createNote,
  updateNote,
  deleteNote,
  deleteNotesByTodoId,
  login,
  register,
  logout,
  updateEmail,
  updatePassword,
  fetchTodoListsRange,
  fetchBootstrap,
  getDocument,
  putDocument,
  deleteDocument,
  pullAndReplaceCache,
  flushOrder,
  nextFlushOrder,
  mergeLiveDirty,
  metaAfterPartialFlush,
  flush,
};
